/** Desglose del dinero de un pago (`breakdown` de `GET /payments/admin/payments`). */
export interface PaymentBreakdown {
  /** Total cobrado al comprador. */
  total?: number;
  totalCents?: number;
  /** Precio base con el que el vendedor anunció la pieza. */
  piece?: number;
  pieceCents?: number;
  /** Comisión de la app que paga el comprador, sobre la base. */
  appFee?: number;
  appFeeCents?: number;
  /** % con el que se cobró ESTE pago (histórico; no cambia con el tarifario). */
  appFeePercent?: number | null;
  shipping?: number;
  shippingCents?: number;
  /** Comisión que se le descuenta al vendedor. */
  sellerCommission?: number;
  sellerCommissionCents?: number;
  /** % de comisión del vendedor con el que se cobró este pago. */
  sellerCommissionPercent?: number | null;
  /** Lo que termina recibiendo el vendedor. */
  sellerPayout?: number;
  sellerPayoutCents?: number;
  /** Lo que Stripe nos descontó por procesar el cobro. */
  stripeFee?: number | null;
  stripeFeeCents?: number | null;
  /** IVA incluido en la comisión de Stripe (acreditable). */
  stripeFeeTaxCents?: number | null;
  /** Lo que realmente entró a la cuenta (total − comisión Stripe). */
  stripeNet?: number | null;
  stripeNetCents?: number | null;
  platformRevenue?: number;
  platformRevenueCents?: number;
}

/** Cronología del pedido: de la solicitud de la pieza al payout. */
export interface PaymentTimeline {
  partRequestedAt?: string | null;
  offerPublishedAt?: string | null;
  awardedAt?: string | null;
  paidAt?: string | null;
  deliveredAt?: string | null;
  autoReleaseAt?: string | null;
  payoutCreatedAt?: string | null;
  payoutPaidAt?: string | null;
  updatedAt?: string | null;
}

/**
 * De dónde viene el pago. El backend los manda ANIDADOS aquí (`relations` de
 * `mapPaymentRow`), no en la raíz del pago: es lo que permite volver de un
 * cobro a su subasta sin consultas extra.
 */
export interface PaymentRelations {
  offerId?: number | null;
  requestedPartId?: number | null;
  auctionId?: number | null;
}

/** Pago de la plataforma (la BD guarda centavos; la API expone ambos). */
export interface Payment {
  id: number;
  userId?: number;
  buyerName?: string;
  buyerEmail?: string;
  sellerUserId?: number;
  sellerName?: string;
  sellerEmail?: string;
  /** Vehículo, pieza y oferta de los que salió este pago. */
  relations?: PaymentRelations;
  /** @deprecated El backend los manda en `relations`; usa `paymentOfferId()`. */
  offerId?: number;
  partTitle?: string;
  title?: string;
  amount?: number;
  amountCents?: number;
  fee?: number;
  feeCents?: number;
  commission?: number;
  commissionCents?: number;
  /** Comisión que Stripe nos descontó por procesar este cobro. */
  stripeFee?: number | null;
  /** Lo devuelto al comprador hasta ahora. */
  refunded?: number;
  refundedCents?: number;
  /** Retenido por contracargos abiertos/perdidos. */
  disputed?: number;
  disputedCents?: number;
  /** Lo que aún se podría devolver. */
  remainingRefundable?: number;
  remainingRefundableCents?: number;
  refundCount?: number;
  disputeCount?: number;
  /** Ya existe guía: el envío es costo hundido. */
  shippingIncurred?: boolean;
  /** Precio base con el que el vendedor anunció la pieza. */
  offerPrice?: number;
  /** Precio que vio el comprador (base + la comisión de ese momento). */
  offerPriceEnd?: number;
  breakdown?: PaymentBreakdown;
  timeline?: PaymentTimeline;
  status?: string;
  orderStatus?: string;
  order_status?: string;
  stripePaymentIntentId?: string;
  paymentIntentId?: string;
  stripeChargeId?: string | null;
  deliveredConfirmedAt?: string | null;
  trackingNumber?: string;
  shipment?: Record<string, unknown>;
  payout?: Partial<Payout> | null;
  refund?: Partial<Refund> | null;
  createdAt?: string;
  [key: string]: unknown;
}

/** Payout (transferencia al vendedor). */
export interface Payout {
  id: number;
  paymentId?: number;
  sellerUserId?: number;
  sellerName?: string;
  amount?: number;
  amountCents?: number;
  /** Monto antes de reembolsos parciales. */
  originalAmountCents?: number | null;
  /** Lo ya recuperado del vendedor (reversas del transfer). */
  reversedCents?: number;
  status?: string;
  paymentStatus?: string;
  onHold?: boolean | number;
  on_hold?: boolean | number;
  holdReason?: string;
  approvedBy?: number;
  notes?: string | null;
  failureReason?: string | null;
  transferId?: string;
  autoReleaseAt?: string | null;
  deliveredConfirmedAt?: string | null;
  /** La entrega ya está confirmada (requisito para liberar sin `force`). */
  delivered?: boolean;
  openClaims?: number;
  openDisputes?: number;
  partTitle?: string | null;
  /** Fecha en que el payout se transfirió al vendedor. */
  paidAt?: string | null;
  createdAt?: string;
  seller?: { userId?: number; name?: string | null; email?: string; canReceivePayouts?: boolean };
  [key: string]: unknown;
}

/** Quién absorbe un reembolso. */
export type RefundBearer = 'proportional' | 'platform' | 'seller' | 'shipping';

export const REFUND_BEARERS: RefundBearer[] = ['proportional', 'platform', 'seller', 'shipping'];

export const REFUND_BEARER_LABELS: Record<RefundBearer, string> = {
  proportional: 'Proporcional (cada apartado devuelve su parte)',
  platform: 'Lo absorbe la plataforma (cortesía)',
  seller: 'Lo absorbe el vendedor (su culpa)',
  shipping: 'Solo el envío cobrado',
};

export const REFUND_BEARER_HINTS: Record<RefundBearer, string> = {
  proportional:
    'Vendedor, envío, IVA y ganancia devuelven en proporción a lo que recibió cada uno. El depósito del vendedor se reduce; si ya se le pagó, se le recupera.',
  platform: 'El vendedor cobra íntegro y el envío no se toca: baja la ganancia de la plataforma (y su IVA).',
  seller: 'Sale de la parte del vendedor; si no alcanza, el resto lo paga la plataforma.',
  shipping: 'Devuelve el envío cobrado (DHL no recogió, sobrecobro…). No toca al vendedor.',
};

export const REFUND_SOURCE_LABELS: Record<string, string> = {
  admin: 'Admin',
  seller_reject: 'Vendedor rechazó',
  cron_no_shipment: 'Automático: sin envío',
  return: 'Devolución',
  stripe: 'Dashboard de Stripe',
  legacy: 'Histórico',
};

export const PAYOUT_ACTION_LABELS: Record<string, string> = {
  none: 'No afecta al depósito',
  reduced: 'Se redujo el depósito',
  canceled: 'Se canceló el depósito',
  reversed: 'Se recuperó el dinero del vendedor',
  partially_reversed: 'Se recuperó parte del dinero del vendedor',
  pending_reversal: 'Se recuperará si se pierde el contracargo',
};

/** Reembolso (una fila por reembolso; un pago puede tener varios). */
export interface Refund {
  id: number;
  paymentId?: number;
  amount?: number;
  amountCents?: number;
  originalAmount?: number;
  originalAmountCents?: number;
  paymentRefundedCents?: number;
  reason?: string;
  /** Estado del reembolso: pending | succeeded | failed | canceled. */
  status?: string;
  paymentStatus?: string;
  stripeRefundId?: string;
  stripeStatus?: string | null;
  bearer?: RefundBearer | string;
  source?: string;
  adminUserId?: number | null;
  adminName?: string | null;
  sellerPortion?: number;
  sellerPortionCents?: number;
  shippingLossCents?: number;
  stripeFeeLossCents?: number;
  payoutAction?: string | null;
  transferReversalId?: string | null;
  failureReason?: string | null;
  buckets?: Record<string, number> | null;
  partTitle?: string | null;
  buyerName?: string | null;
  createdAt?: string;
  [key: string]: unknown;
}

/** Cuánto se puede devolver aún de un pago (`GET /payments/refunds/:id/refundable`). */
export interface RefundableInfo {
  amountCents: number;
  refundedCents: number;
  pendingCents: number;
  remainingCents: number;
  amount: number;
  refunded: number;
  pending: number;
  remaining: number;
  bearers: string[];
}

/** Contracargo de Stripe. */
export interface Dispute {
  id: number;
  paymentId?: number | null;
  stripeDisputeId?: string;
  amount?: number;
  amountCents?: number;
  fee?: number;
  feeCents?: number;
  currency?: string;
  status?: string;
  reason?: string | null;
  fundsWithdrawnAt?: string | null;
  fundsReinstatedAt?: string | null;
  closedAt?: string | null;
  evidenceDueBy?: string | null;
  sellerPortionCents?: number;
  payoutAction?: string | null;
  transferReversalId?: string | null;
  paymentStatus?: string;
  buyerName?: string | null;
  sellerName?: string | null;
  partTitle?: string | null;
  isOpen?: boolean;
  createdAt?: string;
  [key: string]: unknown;
}

export interface DisputesSummary {
  openCount?: number;
  openAmount?: number;
  lostCount?: number;
  lostAmount?: number;
  wonCount?: number;
  feeAmount?: number;
}

export const DISPUTE_STATUS_LABELS: Record<string, string> = {
  warning_needs_response: 'Aviso: requiere respuesta',
  warning_under_review: 'Aviso: en revisión',
  warning_closed: 'Aviso cerrado',
  needs_response: 'Requiere respuesta',
  under_review: 'En revisión',
  won: 'Ganada',
  lost: 'Perdida',
};

/** Etiquetas en español de los apartados del ledger (los códigos son internos). */
export const LEDGER_ACCOUNT_LABELS: Record<string, string> = {
  PLATFORM: 'Ganancias',
  SELLER: 'Vendedores',
  SHIPPING: 'Envíos',
  TAX: 'IVA',
  ISR: 'ISR (RESICO)',
  EXPENSES: 'Gastos',
  STRIPE_FEES: 'Comisión Stripe',
};

export const LEDGER_ACCOUNTS = ['PLATFORM', 'SELLER', 'SHIPPING', 'TAX', 'ISR', 'EXPENSES', 'STRIPE_FEES'] as const;

/** Balance de un apartado del ledger (`buckets` del dashboard financiero). */
export interface LedgerBucket {
  code?: string;
  name?: string;
  description?: string;
  currency?: string;
  balance?: number;
  balanceCents?: number;
}

/** Etiquetas en español de los tipos de asiento del ledger. */
export const LEDGER_KIND_LABELS: Record<string, string> = {
  allocation: 'Reparto de cobro',
  refund: 'Reembolso',
  payout: 'Depósito al vendedor',
  payout_reversal: 'Reversa de depósito',
  adjustment: 'Corrección manual',
  stripe_fee: 'Comisión de Stripe (cobrada después)',
  dispute: 'Contracargo: Stripe retiró el dinero',
  dispute_fee: 'Multa por contracargo',
  dispute_reversal: 'Contracargo ganado: reintegro',
  platform_fee: 'Comisión de Stripe (plataforma)',
  reclass: 'Reclasificación entre apartados',
  shipping_cost: 'Pago a paquetería (banco)',
  tax_payment: 'Impuesto pagado al SAT (banco)',
  expense_payment: 'Gasto pagado (banco)',
  owner_withdrawal: 'Retiro de utilidades (banco)',
};

/** Salidas de dinero pagadas desde el banco (fuera de Stripe). */
export const EXTERNAL_KINDS = ['shipping_cost', 'tax_payment', 'expense_payment', 'owner_withdrawal'] as const;
export type ExternalKind = (typeof EXTERNAL_KINDS)[number];

export const EXTERNAL_KIND_LABELS: Record<ExternalKind, string> = {
  shipping_cost: 'Pago a la paquetería (DHL)',
  tax_payment: 'Impuesto pagado al SAT (IVA o ISR)',
  expense_payment: 'Gasto pagado',
  owner_withdrawal: 'Retiro de utilidades',
};

/** Apartado natural de cada salida externa. */
export const EXTERNAL_KIND_ACCOUNT: Record<ExternalKind, string> = {
  shipping_cost: 'SHIPPING',
  tax_payment: 'TAX',
  expense_payment: 'EXPENSES',
  owner_withdrawal: 'PLATFORM',
};

/** Asiento del ledger (inmutable). */
export interface LedgerEntry {
  id: number;
  account?: string;
  accountCode?: string;
  type?: string;
  kind?: string;
  amount?: number;
  amountCents?: number;
  currency?: string;
  reference?: string | null;
  memo?: string | null;
  paymentId?: number | null;
  idempotencyKey?: string | null;
  adminUserId?: number | null;
  adminName?: string | null;
  isExternal?: boolean;
  createdAt?: string;
  [key: string]: unknown;
}

/** Fila del resumen del ledger (`groupBy=month|day`). */
export interface LedgerSummaryRow {
  period?: string;
  date?: string;
  account?: string;
  total?: number;
  totalCents?: number;
  count?: number;
  inflow?: number;
  outflow?: number;
  [key: string]: unknown;
}

/** Lo que Stripe nos ha descontado por procesar cobros (dashboard financiero). */
export interface StripeFeesSummary {
  totalCents?: number;
  totalAmount?: number;
  taxCents?: number;
  taxAmount?: number;
  currentMonthCents?: number;
  currentMonthAmount?: number;
  /** Pagos con la comisión ya capturada. */
  capturedCount?: number;
  /** Pagos exitosos sin comisión capturada (pide correr el backfill). */
  missingCount?: number;
}

/** Resultado de la fórmula de conciliación (centavos). */
export interface ReconciliationResult {
  platformCashCents: number;
  bankWithdrawnCents: number;
  externalPaidCents: number;
  expectedStripeCents: number;
  actualStripeCents: number;
  differenceCents: number;
  unmatchedNetCents: number;
  unexplainedCents: number;
  balanced: boolean;
}

/** Bloque `reconciliation` del dashboard (centavos + pesos). */
export interface ReconciliationBlock extends ReconciliationResult {
  platformCash?: number;
  bankWithdrawn?: number;
  externalPaid?: number;
  expectedStripe?: number;
  actualStripe?: number;
  difference?: number;
  unexplained?: number;
  unmatchedCount?: number;
  lastSyncAt?: string | null;
  paymentsWithoutAllocation?: number;
  paymentsMissingFee?: number;
  pendingRefunds?: number;
  openDisputes?: number;
  webhooks?: WebhookStats;
  stripeError?: string | null;
}

export interface WebhookStats {
  processed?: number;
  failed?: number;
  processing?: number;
  last24h?: number;
}

/** `GET /payments/admin/reconciliation`. */
export interface ReconciliationSummary {
  currency: string;
  stripe: { availableCents: number; pendingCents: number; error?: string };
  ledger: Record<string, number>;
  result: ReconciliationResult;
  bankPayouts: { count: number; netCents: number; lastAt: string | null };
  unmatched: { count: number; netCents: number };
  mirror: { count: number; lastSyncAt: string | null; oldestAt: string | null };
  webhooks: WebhookStats;
  paymentsWithoutAllocation: number;
  paymentsMissingFee: number;
  pendingRefunds: number;
  openDisputes: number;
}

/** Balance transaction de Stripe espejada. */
export interface BalanceTransaction {
  id: string;
  type?: string;
  reportingCategory?: string | null;
  amount?: number;
  amountCents?: number;
  fee?: number;
  feeCents?: number;
  net?: number;
  netCents?: number;
  currency?: string;
  sourceId?: string | null;
  description?: string | null;
  status?: string | null;
  availableOn?: string | null;
  stripeCreatedAt?: string;
  matchStatus?: 'matched' | 'unmatched' | 'ignored' | string;
  matchKind?: string | null;
  matchRef?: string | null;
  matchNote?: string | null;
  syncedAt?: string;
  [key: string]: unknown;
}

export const BT_TYPE_LABELS: Record<string, string> = {
  charge: 'Cobro',
  payment: 'Cobro',
  refund: 'Reembolso',
  payment_refund: 'Reembolso',
  transfer: 'Envío de dinero al vendedor',
  transfer_refund: 'Dinero recuperado del vendedor',
  transfer_cancel: 'Envío al vendedor cancelado',
  transfer_failure: 'Falló el envío al vendedor',
  payout: 'Retiro al banco',
  payout_cancel: 'Retiro cancelado',
  payout_failure: 'Retiro fallido',
  adjustment: 'Ajuste hecho por Stripe',
  stripe_fee: 'Comisión de Stripe',
  tax_fee: 'IVA de la comisión de Stripe',
};

export const MATCH_KIND_LABELS: Record<string, string> = {
  payment: 'Pago',
  refund: 'Reembolso',
  payout: 'Depósito al vendedor',
  payout_reversal: 'Dinero recuperado del vendedor',
  bank_payout: 'Retiro al banco',
  dispute: 'Contracargo',
  dispute_reversal: 'Contracargo ganado',
  dispute_fee: 'Multa por contracargo',
  platform_fee: 'Cuota de la plataforma',
  manual: 'Manual',
};

/** Costo real de una guía vs lo cobrado al comprador. */
export interface ShippingCost {
  id: number;
  paymentId?: number;
  shipmentId?: number | null;
  kind?: string;
  trackingNumber?: string | null;
  quoted?: number;
  quotedCents?: number;
  cost?: number;
  costCents?: number;
  costSource?: string;
  currency?: string;
  carrierInvoiceRef?: string | null;
  settledAt?: string | null;
  settledBy?: number | null;
  notes?: string | null;
  margin?: number;
  marginCents?: number;
  partTitle?: string | null;
  paymentStatus?: string;
  orderStatus?: string | null;
  createdAt?: string;
  [key: string]: unknown;
}

export interface ShippingSummary {
  shippingBalance?: number;
  shippingBalanceCents?: number;
  unsettledCount?: number;
  unsettledCost?: number;
  unsettledCostCents?: number;
  unsettledQuotedCents?: number;
  uncommitted?: number;
  uncommittedCents?: number;
}

/** Verificación de invariantes contables de un pago. */
export interface InvariantCheck {
  code: string;
  ok: boolean;
  severity: 'error' | 'warning';
  message: string;
  expected?: number;
  actual?: number;
}

export interface InvariantResult {
  paymentId: number;
  ok: boolean;
  errors: number;
  warnings: number;
  checks: InvariantCheck[];
  balances: Record<string, number>;
  pool: Record<string, number>;
}

export interface AuditViolation {
  paymentId: number;
  status: string;
  amountCents: number;
  errors: number;
  warnings: number;
  problems: string[];
}

export interface AuditSummary {
  scanned: number;
  ok: number;
  withErrors: number;
  withWarnings: number;
  violations: AuditViolation[];
  ranAt: string;
}

/** Expediente contable de un pago (`GET /payments/admin/payments/:id`). */
export interface PaymentDetail {
  payment: Payment;
  ledger: LedgerEntry[];
  pool: Record<string, number>;
  refunds: Refund[];
  disputes: Dispute[];
  shippingCosts: ShippingCost[];
  invariant: InvariantResult | null;
  bearers: string[];
  /** Factura que el vendedor le entregó al comprador (null = aún no la sube). */
  sellerInvoice?: SellerInvoice | null;
}

/** Factura de la pieza subida por el vendedor (`/orders/:id/seller-invoice`). */
export interface SellerInvoice {
  id: number;
  pdfUrl: string | null;
  pdfName: string | null;
  xmlUrl: string | null;
  xmlName: string | null;
  notes: string | null;
  versions: number;
  uploadedAt: string;
  updatedAt: string;
  buyerViewedAt: string | null;
}

/** Dashboard financiero (`GET /payments/admin/dashboard`). Forma flexible. */
export interface FinanceDashboard {
  stripeBalance?: Record<string, unknown>;
  accounts?: Record<string, unknown> | unknown[];
  /** Balances por apartado del ledger. */
  buckets?: LedgerBucket[];
  payouts?: Record<string, unknown>;
  refunds?: Record<string, unknown>;
  month?: Record<string, unknown>;
  stripeFees?: StripeFeesSummary;
  shipping?: ShippingSummary;
  reconciliation?: ReconciliationBlock;
  [key: string]: unknown;
}

/** Asiento manual del ledger. */
export type LedgerAdjustmentType = 'reclass' | 'external' | 'correction';

export interface LedgerAdjustmentRequest {
  type: LedgerAdjustmentType;
  accountCode: string;
  /** Destino de una reclasificación. */
  counterAccountCode?: string;
  /** Tipo de salida externa. */
  kind?: ExternalKind;
  amount?: number;
  amountCents?: number;
  reference?: string;
  memo?: string;
  paymentId?: number | null;
}
